import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import type {
  Type_availableShifts,
  availableShifts as AvailableShifts,
} from "@vTypes";
import {
  ArrowDown,
  Check,
  CircleArrowLeft,
  CircleArrowRight,
  Search,
  X,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { getDefaultShiftsSortedBySequence } from "@/utils/shift-util";
import useRosterConfig from "@/hooks/useRosterConfig";
import CustomDragNDropModal from "@/components/modal/CustomDragNDropModal";
import ShiftCode from "@/components/shift/ShiftCode";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RosterModifyShiftButton } from "./RosterModifyShiftButton";

type RosterModifyTabMode = "assignment" | "banned";

function getShiftTypeLabel(
  shift: Type_availableShifts | AvailableShifts
): string {
  const shiftType = "type" in shift ? shift.type?.trim() : undefined;
  return shiftType || "근무";
}

function getHighlightedText(value: string, query: string): ReactNode {
  if (!query) return value;

  const start = value.toLocaleLowerCase("ko-KR").indexOf(query);
  if (start < 0) return value;

  const end = start + query.length;
  return (
    <>
      {value.slice(0, start)}
      <mark className="rounded-sm bg-sig/15 px-0.5 font-semibold text-inherit">
        {value.slice(start, end)}
      </mark>
      {value.slice(end)}
    </>
  );
}

/** 동일 shift_id가 여러 행일 때 선택·하이라이트는 DB id로 구분 */
function isSameShiftRow(
  selected: Type_availableShifts | null,
  shift: Type_availableShifts
): boolean {
  if (!selected) return false;
  const a = selected as Type_availableShifts & { id?: number };
  const b = shift as Type_availableShifts & { id?: number };
  if (
    a.id != null &&
    b.id != null &&
    Number.isFinite(Number(a.id)) &&
    Number.isFinite(Number(b.id))
  ) {
    return Number(a.id) === Number(b.id);
  }
  return selected.shift_id === shift.shift_id;
}

function shiftRowKey(
  shift: Type_availableShifts & { id?: number },
  index: number
) {
  if (shift.id != null && Number.isFinite(Number(shift.id))) {
    return `shift-row-${shift.id}`;
  }
  return `shift-row-${shift.shift_id}-${index}`;
}

function ModalRosterModify({
  modalClose,
  availableShifts,
  selectedShift,
  setSelectedShift,
  onUndo,
  onRedo,
  title,
  instruction,
  canUndo,
  canRedo,
  portalZIndex = 10,
  portalTarget,
  containerSelector,
  targetRef,
  clearSelectionLabel,
  isClearSelection = false,
  onClearSelection,
  tabMode,
  onTabModeChange,
  clearSelectionOnClose = true,
  isOpen = true,
  selectedShifts,
  onToggleShift,
  maxSelection = 1,
  onResetSelection,
  onActivate,
  positionStorageKey,
}: {
  modalClose: (value: boolean) => void;
  availableShifts: AvailableShifts[] | Type_availableShifts[];
  selectedShift: Type_availableShifts | null;
  setSelectedShift: (value: Type_availableShifts | null) => void;
  deleteAllShift: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  title?: string;
  instruction?: string;
  deleteBtnVersion?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  portalZIndex?: number;
  portalTarget?: string;
  containerSelector?: string;
  targetRef?: React.RefObject<HTMLElement | null>;
  clearSelectionLabel?: string;
  isClearSelection?: boolean;
  onClearSelection?: () => void;
  tabMode?: RosterModifyTabMode;
  onTabModeChange?: (mode: RosterModifyTabMode) => void;
  clearSelectionOnClose?: boolean;
  isOpen?: boolean;
  selectedShifts?: readonly Type_availableShifts[];
  onToggleShift?: (shift: Type_availableShifts) => void;
  maxSelection?: number;
  onResetSelection?: () => void;
  onActivate?: () => void;
  positionStorageKey?: string;
}) {
  const [defaultShifts, setDefaultShifts] = useState<Type_availableShifts[]>(
    []
  );
  const [customShifts, setCustomShifts] = useState<Type_availableShifts[]>([]);
  const [shiftQueryByTab, setShiftQueryByTab] = useState<
    Record<RosterModifyTabMode, string>
  >({ assignment: "", banned: "" });
  const [activeShiftIndexByTab, setActiveShiftIndexByTab] = useState<
    Record<RosterModifyTabMode, number>
  >({ assignment: -1, banned: -1 });
  const shiftSearchId = useId();
  const shiftOptionsId = useId();

  const { data: serverConfig } = useRosterConfig();
  const isUseMid = serverConfig?.use_mid;

  type ShiftWithDefault = Type_availableShifts & {
    default_shift?: string | null;
    sequence?: number;
  };

  // isUseMid false면 M을 아예 제외한 목록만 사용 (default/custom 어디에도 안 나오게)
  // "주"는 배치로 돌아가기 때문에 직접 수정하면 안 되므로 필터링
  const shiftsForModal = useMemo(() => {
    if (!availableShifts?.length) return [];
    let filtered = [...availableShifts] as Type_availableShifts[];

    // "주" 근무코드 필터링 (배치로 돌아가기 때문에 직접 수정 불가)
    filtered = filtered.filter((s) => s.shift_id !== "주");

    // isUseMid false면 M도 제외
    if (isUseMid === false) {
      filtered = filtered.filter(
        (s) => (s as ShiftWithDefault).default_shift !== "M"
      );
    }

    return filtered;
  }, [availableShifts, isUseMid]);

  // default_shift 기준 메인 시프트 (shiftsForModal 이미 M 제외됨)
  const defaultShiftsFromData = useMemo(() => {
    if (!shiftsForModal.length) return [];
    return getDefaultShiftsSortedBySequence(
      shiftsForModal as ShiftWithDefault[],
      { excludeOff: false }
    ) as Type_availableShifts[];
  }, [shiftsForModal]);

  const customShiftsFromData = useMemo(() => {
    if (!shiftsForModal.length) return [];
    const defaultKeys = new Set(
      defaultShiftsFromData.map((s) => {
        const sid = (s as Type_availableShifts & { id?: number }).id;
        return sid != null && Number.isFinite(Number(sid))
          ? `id:${sid}`
          : `sid:${s.shift_id}:${(s as ShiftWithDefault).sequence ?? 0}`;
      })
    );
    return shiftsForModal.filter((s) => {
      const sid = (s as Type_availableShifts & { id?: number }).id;
      const key =
        sid != null && Number.isFinite(Number(sid))
          ? `id:${sid}`
          : `sid:${s.shift_id}:${(s as ShiftWithDefault).sequence ?? 0}`;
      return !defaultKeys.has(key);
    });
  }, [shiftsForModal, defaultShiftsFromData]);

  useEffect(() => {
    setDefaultShifts(defaultShiftsFromData);
    setCustomShifts(customShiftsFromData);
  }, [defaultShiftsFromData, customShiftsFromData]);

  const stableInitialPosition = useMemo(
    () => (targetRef ? undefined : { x: "50%", y: "50%" }),
    [targetRef]
  );

  const isMultipleSelection = tabMode === "banned";
  const activePickerTab = tabMode ?? "assignment";
  const shiftQuery = shiftQueryByTab[activePickerTab];
  const activeShiftIndex = activeShiftIndexByTab[activePickerTab];
  const activeSelections = isMultipleSelection
    ? (selectedShifts ?? [])
    : selectedShift
      ? [selectedShift]
      : [];
  const primarySelection = activeSelections[0] ?? null;
  const hasSelection = activeSelections.length > 0 || isClearSelection;
  const shouldPointToShiftList = primarySelection === null && !isClearSelection;
  const normalizedShiftQuery = shiftQuery.trim().toLocaleLowerCase("ko-KR");
  const orderedShifts = useMemo(
    () => [...defaultShifts, ...customShifts],
    [defaultShifts, customShifts]
  );
  const filteredShifts = useMemo(
    () =>
      orderedShifts.filter((shift) => {
        if (!normalizedShiftQuery) return true;
        return (
          shift.shift_id
            .toLocaleLowerCase("ko-KR")
            .includes(normalizedShiftQuery) ||
          shift.name.toLocaleLowerCase("ko-KR").includes(normalizedShiftQuery)
        );
      }),
    [normalizedShiftQuery, orderedShifts]
  );
  const activeShift = filteredShifts[activeShiftIndex];
  const selectionHint =
    tabMode === "banned"
      ? primarySelection
        ? "표의 근무 셀을 선택해 금지 설정을 반영하세요."
        : isClearSelection
          ? "표의 근무 셀을 선택해 금지 설정을 해제하세요."
          : "금지할 근무코드를 선택하세요."
      : selectedShift
        ? "표의 근무 셀을 선택해 반영하세요."
        : isClearSelection
          ? "표의 근무 셀을 선택해 비우세요."
          : "적용할 근무코드를 선택하세요.";

  const isShiftSelected = (shift: Type_availableShifts) =>
    activeSelections.some((selected) => isSameShiftRow(selected, shift));

  const selectShift = (shift: Type_availableShifts) => {
    if (isMultipleSelection && onToggleShift) {
      onToggleShift(shift);
      return;
    }
    setSelectedShift(isSameShiftRow(selectedShift, shift) ? null : shift);
  };

  const chooseShift = (shift: Type_availableShifts) => {
    selectShift(shift);
    setShiftQueryByTab((previous) => ({
      ...previous,
      [activePickerTab]: "",
    }));
    setActiveShiftIndexByTab((previous) => ({
      ...previous,
      [activePickerTab]: -1,
    }));
  };

  return (
    <CustomDragNDropModal
      portalZIndex={portalZIndex}
      portalTarget={portalTarget}
      containerSelector={containerSelector}
      initialPosition={stableInitialPosition}
      targetRef={targetRef}
      open={isOpen}
      positionStorageKey={positionStorageKey}
    >
      {({ onMouseDown }) => (
        <section
          className="modal_window modal_roster_modify pointer-events-auto inline-flex flex-col overflow-hidden bg-white shadow-[2px_2px_4px_2px_rgba(0,0,0,0.2)]"
          onPointerDownCapture={onActivate}
          onFocusCapture={onActivate}
          style={{
            width: "fit-content",
            minWidth: 0,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "min(80vh, 560px)",
            boxSizing: "border-box",
            position: "static",
            top: "auto",
            left: "auto",
            transform: "none",
          }}
        >
          <header
            className="modal_header modal_moveHandler flex w-full items-center justify-between box-border !bg-[#f0f0f2] !px-4 !py-2"
            onMouseDown={onMouseDown}
          >
            <div className="flex min-w-0 items-center gap-3">
              {(onUndo || onRedo) && (
                <div className="flex shrink-0">
                  {onUndo && (
                    <div
                      className={cn(
                        "mr-2",
                        canUndo
                          ? "cursor-pointer"
                          : "cursor-not-allowed opacity-50"
                      )}
                      onClick={canUndo ? onUndo : undefined}
                    >
                      <CircleArrowLeft
                        size={26}
                        strokeWidth={1}
                        className="text-[#666666]"
                      />
                    </div>
                  )}
                  {onRedo && (
                    <div
                      className={cn(
                        canRedo
                          ? "cursor-pointer"
                          : "cursor-not-allowed opacity-50"
                      )}
                      onClick={canRedo ? onRedo : undefined}
                    >
                      <CircleArrowRight
                        size={26}
                        strokeWidth={1}
                        className="text-[#666666]"
                      />
                    </div>
                  )}
                </div>
              )}

              <h3 className="m-0 truncate text-left text-[18px] font-bold text-[#333333]">
                {title || "근무표 수정하기"}
              </h3>
            </div>
            <button
              className="flex size-8 shrink-0 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-200 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig"
              type="button"
              aria-label="근무 설정 닫기"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={() => {
                modalClose(false);
                if (clearSelectionOnClose) {
                  setSelectedShift(null);
                }
              }}
            >
              <X aria-hidden="true" className="size-5" />
            </button>
          </header>

          {tabMode && onTabModeChange && (
            <Tabs
              value={tabMode}
              onValueChange={(value) => {
                if (value === "assignment" || value === "banned") {
                  onTabModeChange(value);
                }
              }}
              className="w-[min(440px,calc(100vw-32px))] gap-0 border-b border-zinc-200 px-4"
            >
              <TabsList
                aria-label="원티드 설정 유형"
                className="h-10 gap-5 rounded-none !bg-transparent p-0"
              >
                <TabsTrigger
                  value="assignment"
                  className="h-full whitespace-nowrap !rounded-none !border-x-0 !border-t-0 !border-b-2 !border-transparent px-0 text-[13px] font-semibold text-zinc-500 shadow-none hover:text-[#0E59C6] data-[state=active]:!border-b-sig data-[state=active]:!bg-transparent data-[state=active]:text-[#0E59C6] data-[state=active]:shadow-none"
                >
                  근무코드 배정
                </TabsTrigger>
                <TabsTrigger
                  value="banned"
                  className="h-full whitespace-nowrap !rounded-none !border-x-0 !border-t-0 !border-b-2 !border-transparent px-0 text-[13px] font-semibold text-zinc-500 shadow-none hover:text-amber-800 data-[state=active]:!border-b-amber-500 data-[state=active]:!bg-transparent data-[state=active]:text-amber-800 data-[state=active]:shadow-none"
                >
                  금지 근무
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {tabMode ? (
            <div className="w-[min(440px,calc(100vw-32px))] px-4 pt-2 pb-2">
              <div className="flex min-h-9 items-center gap-2 px-0 py-1.5">
                {primarySelection ? (
                  <div className="flex min-w-0 items-center gap-1.5">
                    <ShiftCode
                      code={primarySelection.shift_id}
                      color={primarySelection.color}
                    />
                    <span className="truncate text-xs font-semibold text-zinc-800">
                      {primarySelection.name}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-zinc-500">
                    {isClearSelection ? clearSelectionLabel : "선택 코드 없음"}
                  </span>
                )}
                <button
                  type="button"
                  className="ml-auto flex size-6 shrink-0 items-center justify-center rounded-sm text-zinc-500 transition-colors hover:bg-white/80 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="선택 초기화"
                  title="선택 초기화"
                  disabled={!hasSelection}
                  onClick={onResetSelection}
                >
                  <X aria-hidden="true" className="size-3.5" />
                </button>
              </div>
              <p className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-zinc-600" aria-live="polite">
                {selectionHint}
                {shouldPointToShiftList && (
                  <ArrowDown aria-hidden="true" className="size-3.5 text-zinc-400" />
                )}
              </p>
            </div>
          ) : (
            <div className="flex w-[min(440px,calc(100vw-32px))] items-center justify-center px-6 pt-6 pb-3">
              <p className="font-pretendard">
                {instruction ?? "근무 코드를 선택 후, 입력하고 싶은 날짜를 선택하세요."}
              </p>
            </div>
          )}
          <div className="modal_body !mx-auto w-[min(440px,calc(100vw-32px))] !max-w-none !px-4 !pt-1 !pb-4">
            {tabMode ? (
              <fieldset className="min-w-0">
                <legend className="sr-only">근무코드 검색 및 선택</legend>
                <div className="group relative">
                  <label htmlFor={shiftSearchId} className="sr-only">
                    근무코드 검색
                  </label>
                  <Search
                    aria-hidden="true"
                    className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400 transition-colors group-focus-within:text-sig"
                  />
                  <input
                    id={shiftSearchId}
                    name="wanted-adjustment-shift-search"
                    type="search"
                    autoComplete="off"
                    value={shiftQuery}
                    aria-controls={shiftOptionsId}
                    placeholder="근무코드 또는 이름 검색…"
                    className="h-9 w-full rounded-md border border-zinc-200 bg-zinc-50 py-1.5 pl-9 pr-9 text-sm text-zinc-800 outline-none placeholder:text-xs placeholder:text-zinc-400 transition-[background-color,border-color,box-shadow] hover:border-zinc-300 focus:bg-white focus-visible:border-sig focus-visible:ring-2 focus-visible:ring-sig/20"
                    onChange={(event) => {
                      setShiftQueryByTab((previous) => ({
                        ...previous,
                        [activePickerTab]: event.target.value,
                      }));
                      setActiveShiftIndexByTab((previous) => ({
                        ...previous,
                        [activePickerTab]: -1,
                      }));
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "ArrowDown") {
                        event.preventDefault();
                        setActiveShiftIndexByTab((previous) => ({
                          ...previous,
                          [activePickerTab]: Math.min(
                            previous[activePickerTab] + 1,
                            Math.max(filteredShifts.length - 1, 0)
                          ),
                        }));
                      }
                      if (event.key === "ArrowUp") {
                        event.preventDefault();
                        setActiveShiftIndexByTab((previous) => ({
                          ...previous,
                          [activePickerTab]: Math.max(
                            previous[activePickerTab] - 1,
                            0
                          ),
                        }));
                      }
                      if (event.key === "Enter" && activeShift) {
                        event.preventDefault();
                        chooseShift(activeShift);
                      }
                    }}
                  />
                  {shiftQuery && (
                    <button
                      type="button"
                      aria-label="검색어 지우기"
                      className="absolute right-1.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-sm text-zinc-400 transition-colors hover:bg-zinc-200 hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig"
                      onClick={() => {
                        setShiftQueryByTab((previous) => ({
                          ...previous,
                          [activePickerTab]: "",
                        }));
                        setActiveShiftIndexByTab((previous) => ({
                          ...previous,
                          [activePickerTab]: -1,
                        }));
                      }}
                    >
                      <X aria-hidden="true" className="size-3.5" />
                    </button>
                  )}
                </div>
                <ul
                  id={shiftOptionsId}
                  aria-label="선택 가능한 근무코드"
                  className="mt-2 max-h-72 space-y-1 overflow-y-auto overscroll-contain pr-1"
                >
                  {onClearSelection && clearSelectionLabel && (
                    <li>
                      <button
                        type="button"
                        className={cn(
                          "flex min-h-9 w-full items-center gap-2 rounded-md px-2 text-left text-xs font-medium text-zinc-600 transition-[background-color,box-shadow] hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-inset",
                          isClearSelection && "bg-sky-50 text-sig"
                        )}
                        aria-pressed={isClearSelection}
                        onClick={onClearSelection}
                      >
                        <span translate="no" className="shrink-0">
                          <ShiftCode code="-" />
                        </span>
                        <span>{clearSelectionLabel}</span>
                        {isClearSelection && (
                          <Check
                            aria-label="선택됨"
                            className="ml-auto size-4 shrink-0 text-sig"
                          />
                        )}
                      </button>
                    </li>
                  )}
                  {filteredShifts.length > 0 ? (
                    filteredShifts.map((shift, index) => {
                      const isSelected = isShiftSelected(shift);
                      const isCodeMatched = shift.shift_id
                        .toLocaleLowerCase("ko-KR")
                        .includes(normalizedShiftQuery);
                      const isDisabled =
                        isMultipleSelection &&
                        maxSelection > 1 &&
                        !isSelected &&
                        activeSelections.length >= maxSelection;
                      return (
                        <li
                          key={shiftRowKey(
                            shift as Type_availableShifts & { id?: number },
                            index
                          )}
                        >
                          <button
                            type="button"
                            disabled={isDisabled}
                            aria-pressed={isSelected}
                            className={cn(
                              "flex min-h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm transition-[background-color,box-shadow] hover:bg-light-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50",
                              (index === activeShiftIndex || isSelected) &&
                                (tabMode === "banned"
                                  ? "bg-amber-50"
                                  : "bg-sky-50")
                            )}
                            aria-label={`근무코드 ${shift.shift_id} ${shift.name}`}
                            onClick={() => chooseShift(shift)}
                          >
                            <span
                              translate="no"
                              className={cn(
                                "inline-flex shrink-0 rounded-[5px]",
                                normalizedShiftQuery &&
                                  isCodeMatched &&
                                  "ring-2 ring-sig/45 ring-offset-1"
                              )}
                            >
                              <ShiftCode
                                code={shift.shift_id}
                                color={shift.color}
                              />
                            </span>
                            <span className="min-w-0 truncate font-medium text-zinc-800">
                              {getHighlightedText(
                                shift.name,
                                normalizedShiftQuery
                              )}
                            </span>
                            <span className="shrink-0 whitespace-nowrap text-[11px] font-medium text-zinc-400">
                              · {getShiftTypeLabel(shift)}
                            </span>
                            {isSelected && (
                              <Check
                                aria-label="선택됨"
                                className={cn(
                                  "ml-auto size-4 shrink-0",
                                  tabMode === "banned"
                                    ? "text-amber-700"
                                    : "text-sig"
                                )}
                              />
                            )}
                          </button>
                        </li>
                      );
                    })
                  ) : (
                    <li className="px-2 py-4 text-center text-xs text-zinc-500">
                      검색 결과가 없습니다.
                    </li>
                  )}
                </ul>
              </fieldset>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,40px)] justify-start gap-2">
                {onClearSelection && clearSelectionLabel && (
                  <button
                    type="button"
                    className={cn(
                      "button_inputShiftCode relative",
                      isClearSelection && "selected"
                    )}
                    title={clearSelectionLabel}
                    aria-label={clearSelectionLabel}
                    aria-pressed={isClearSelection}
                    onClick={onClearSelection}
                  >
                    <ShiftCode code="-" />
                    {isClearSelection && (
                      <span
                        className="pointer-events-none absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full border-2 border-white bg-[#0E59C6] text-white shadow-sm"
                        aria-hidden="true"
                      >
                        <Check size={12} strokeWidth={3} />
                      </span>
                    )}
                  </button>
                )}
                {orderedShifts.map((shift, index) => {
                  const isSelected = isShiftSelected(shift);
                  return (
                    <RosterModifyShiftButton
                      key={shiftRowKey(
                        shift as Type_availableShifts & { id?: number },
                        index
                      )}
                      shift={shift}
                      isSelected={isSelected}
                      disabled={
                        isMultipleSelection &&
                        maxSelection > 1 &&
                        !isSelected &&
                        activeSelections.length >= maxSelection
                      }
                      onSelect={() => selectShift(shift)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}
    </CustomDragNDropModal>
  );
}

export default ModalRosterModify;
